/**
 * StatePicker — ask-once "which state do you teach in?" modal (IIG Phase 4).
 *
 * Also hosts `useSchoolContext`, the session-cached hook that reads the teacher's
 * school/state from be_login (`GET /api/user/school-context`) and writes it back
 * (`PUT /api/user/school-state`). The Phase-4 spec places this hook in
 * `services/useSchoolContext.js`; that path is outside this cluster's file scope,
 * so it lives here (the state picker is its only mandatory consumer) and the chip
 * placements import it from here. Everything degrades invisibly when the auth
 * worker is unreachable — no context → no modal, no viewer-state-aware chips.
 */

import { API_BASE } from '../../config.js';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

// Brightivity: the teacher's state lives on brightivity.dev/auth/me (shared user_details.school_state).
const AUTH_API = `${API_BASE}/auth`;

// The 51 jurisdictions (50 states + DC) — same set as be_login's us-states map.
export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

// Module scope — one school-context fetch per app session, shared across every
// hook consumer (chips + picker) so we never re-fetch or double-prompt.
let sessionCache = null;

// …and one SHARED subscription, which is the load-bearing half.
//
// The cache alone is not enough: every hook instance holds its own
// `useState(sessionCache)`, so a save made through one instance used to
// re-render ONLY that instance. Every other mounted consumer kept a stale copy
// for the rest of the session. That is how changing the country in the account
// modal left Printables on the old jurisdiction until a reload — `jurisdiction`
// in useCatalogContext resolves as `stateCode || localCode`, so the stale
// stateCode shadowed the fresh pick the modal had just broadcast, and the
// mirror-down effect then wrote the stale code back over it.
//
// publish() is the ONLY way to move the cache. Never assign sessionCache
// directly, or the consumer that did not make the change goes stale again.
const subscribers = new Set();

function publish(next) {
  sessionCache = next;
  subscribers.forEach((fn) => { try { fn(next); } catch { /* a torn-down consumer must not block the rest */ } });
}

export function useSchoolContext() {
  const { user } = useAuth();
  const token = user?.token || null;
  const [context, setContext] = useState(sessionCache);

  // Every instance follows the shared store, whoever writes to it.
  useEffect(() => {
    subscribers.add(setContext);
    // A publish between this instance's render and this effect would be missed.
    if (sessionCache !== context) setContext(sessionCache);
    return () => { subscribers.delete(setContext); };
    // Mount-only by design: setContext is stable, and re-subscribing on every
    // `context` change would churn the set on every publish.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token || sessionCache) return;
    let cancelled = false;
    fetch(`${AUTH_API}/me/school-context`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j?.success) return;
        publish(j);
      })
      .catch(() => { /* offline → chips/picker simply don't render */ });
    return () => { cancelled = true; };
  }, [token]);

  const saveState = useCallback(async (stateCode) => {
    const code = String(stateCode).toUpperCase();

    // Publish OPTIMISTICALLY, before the network call. The await used to leave a
    // window in which the jurisdiction event had already reached Printables but
    // stateCode was still the old country — long enough for the mirror-down
    // effect in useCatalogContext to revert the pick (and the localStorage
    // mirror with it). Flipping the shared store first closes that window; the
    // server response only reconciles the display name below.
    publish({
      ...(sessionCache || {}),
      success: true,
      schoolState: code,
      schoolStateCode: code,
      source: 'user_details',
    });

    let j = null;
    try {
      const r = await fetch(`${AUTH_API}/me/school-state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stateCode }),
      });
      j = await r.json().catch(() => null);
    } catch {
      j = null; // offline — the optimistic publish above still carries the choice
    }
    publish({
      ...(sessionCache || {}),
      success: true,
      schoolState: j?.schoolState ?? code,
      schoolStateCode: j?.schoolStateCode ?? code,
      source: 'user_details',
    });
    return j;
  }, [token]);

  // Clearing the country. `PUT /api/user/school-state` has no clear semantics,
  // so this is session-local only — but without it `stateCode` kept the old
  // server value and "no region" was simply unselectable: useCatalogContext
  // reads `stateCode || localCode`, and a stale stateCode wins over a cleared
  // mirror exactly the way it did over a fresh pick.
  const clearState = useCallback(() => {
    publish({ ...(sessionCache || {}), success: true, schoolState: null, schoolStateCode: null });
  }, []);

  // `skipped` is React state (seeded from localStorage) so skip() closes the
  // modal immediately without needing a remount.
  const [skipped, setSkipped] = useState(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem('pse_state_picker_skipped') === '1');
  const skip = useCallback(() => {
    try { localStorage.setItem('pse_state_picker_skipped', '1'); } catch { /* ignore */ }
    setSkipped(true);
  }, []);

  return {
    context,
    stateCode: context?.schoolStateCode ?? null,
    loaded: !!context,
    saveState,
    clearState,
    skipped,
    skip,
  };
}

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(27,37,80,0.24)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10060,
  fontFamily: 'inherit',
};

const modalStyle = {
  background: 'linear-gradient(145deg, #FFFFFF 0%, #FFFFFF 100%)',
  border: '1px solid rgba(27,37,80,0.095)',
  borderRadius: '20px',
  padding: '32px',
  width: '460px', maxWidth: '90vw',
  boxShadow: '0 25px 50px rgba(27,37,80,0.17)',
};

// Build the grouped option list: fetched jurisdictions (US states + territories
// + countries) when available, else the offline US fallback. Countries and US
// jurisdictions go in separate optgroups for scannability (LatAm expansion).
export function groupJurisdictions(jurisdictions) {
  const list = (jurisdictions && jurisdictions.length)
    ? jurisdictions
    : US_STATES.map((s) => ({ ...s, jurisdictionType: 'us_state' }));
  const us = list.filter((j) => j.jurisdictionType !== 'country').sort((a, b) => a.name.localeCompare(b.name));
  const countries = list.filter((j) => j.jurisdictionType === 'country').sort((a, b) => a.name.localeCompare(b.name));
  return { us, countries };
}

export default function StatePicker({ open, onSave, onSkip, onDismiss, jurisdictions }) {
  const { t } = useTranslation('home');
  const [selected, setSelected] = useState('');
  const { us, countries } = groupJurisdictions(jurisdictions);

  // 2026-07-14 (sweep #7) — Esc dismisses for the session (never permanent).
  useEffect(() => {
    if (!open || !onDismiss) return;
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onClick={() => onDismiss?.()}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ color: '#1B2550', fontSize: '20px', fontWeight: 600, marginBottom: '6px', textAlign: 'center' }}>
          {t('statePicker.title')}
        </div>
        <div style={{ color: '#545A78', fontSize: '13px', textAlign: 'center', marginBottom: '24px', lineHeight: 1.5 }}>
          {t('statePicker.subtitle')}
        </div>

        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: '12px',
            background: '#FFFFFF', border: '1px solid rgba(27,37,80,0.114)',
            color: '#1B2550', fontSize: '14px', outline: 'none', cursor: 'pointer',
            marginBottom: '20px', boxSizing: 'border-box',
            // Native dropdown popup renders LIGHT unless the control opts into
            // the dark scheme — without this the option list is white-on-white
            // (same fix as the admin dark-theme form controls).
            colorScheme: 'dark',
          }}
        >
          <option value="" style={{ background: '#FFFFFF', color: '#1B2550' }}>{t('statePicker.selectPlaceholder')}</option>
          {countries.length > 0 && (
            <optgroup label={t('statePicker.groupCountries')} style={{ background: '#FFFFFF', color: '#1B2550' }}>
              {countries.map((s) => (
                <option key={s.code} value={s.code} style={{ background: '#FFFFFF', color: '#1B2550' }}>{s.name}</option>
              ))}
            </optgroup>
          )}
          <optgroup label={t('statePicker.groupUnitedStates')} style={{ background: '#FFFFFF', color: '#1B2550' }}>
            {us.map((s) => (
              <option key={s.code} value={s.code} style={{ background: '#FFFFFF', color: '#1B2550' }}>{s.name}</option>
            ))}
          </optgroup>
        </select>

        <button
          onClick={() => selected && onSave?.(selected)}
          disabled={!selected}
          style={{
            width: '100%', padding: '13px', borderRadius: '12px', border: 'none',
            background: selected ? 'linear-gradient(135deg, #189A57 0%, #189A57 100%)' : 'rgba(27,37,80,0.048)',
            color: selected ? '#fff' : '#8A8FA8',
            fontSize: '14px', fontWeight: 600,
            cursor: selected ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease', marginBottom: '10px',
          }}
        >
          {t('common:actions.save')}
        </button>
        <button
          onClick={() => onSkip?.()}
          style={{
            width: '100%', padding: '10px', borderRadius: '12px',
            background: 'transparent', border: 'none',
            color: '#545A78', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}
        >
          {t('statePicker.skipForNow')}
        </button>
      </div>
    </div>
  );
}
